import React from "react";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Check, Users, MessageSquare, Shield, Star, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

export default function ClientManagementPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Client Management Software for Cleaning Companies"
        description="Manage client relationships from first contact to loyal partner. Track leads, onboard locations, and monitor client satisfaction with ManageClean."
        keywords={[
          "client management",
          "CRM for cleaning companies",
          "janitorial client tracking",
          "cleaning business CRM",
        ]}
        url="/features/client-management"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Features", url: "/features/client-management" },
            { name: "Client Management", url: "/features/client-management" },
          ]),
          schemas.createFeatureModuleSchema({
            name: "Client Management & CRM",
            description:
              "Manage commercial cleaning client relationships from lead intake to multi-location contract execution and customer satisfaction reviews.",
            url: "/features/client-management",
            featureList: [
              "Multi-Location Account Hierarchy",
              "Facility Specs & Security Access Storage",
              "Automated Client Onboarding Workflows",
              "Client Review & Satisfaction Tracking",
              "Direct Client Messaging & Notifications",
            ],
          }),
        ]}
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-20 pt-32">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-12 lg:flex-row">
            <div className="space-y-8 lg:w-1/2">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600">
                <Users className="h-4 w-4" />
                <span>Client Relationship Management</span>
              </div>

              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                Master Your{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Client Relationships
                </span>
              </h1>

              <p className="max-w-xl text-lg leading-relaxed text-slate-600">
                From first contact to loyal partner. Manage leads, onboard new locations, and track
                client satisfaction all in one place.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/sign-up">
                  <Button
                    size="lg"
                    className="h-12 rounded-full bg-blue-600 px-8 text-lg hover:bg-blue-700"
                  >
                    Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative lg:w-1/2">
              <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200/50 bg-white/50 shadow-2xl backdrop-blur-sm">
                <img
                  src="/assets/feature_client_management.png"
                  alt="Client Management Dashboard"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-12 -right-12 -z-10 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Deep Dive */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900">
              Complete Client Lifecycle Management
            </h2>
            <p className="text-slate-600">
              Stop using spreadsheets. ManageClean OS gives you a professional CRM designed
              specifically for the cleaning industry.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Centralized CRM</h3>
              <p className="mb-4 text-slate-600">
                Keep all client details, contacts, and service history in one searchable database.
                Never lose a phone number or access code again.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Unlimited Contacts
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Multi-Location Support
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100">
                <Shield className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Onboarding Pipelines</h3>
              <p className="mb-4 text-slate-600">
                Structured workflows to ensure new locations are set up correctly. Track progress
                from "Contract Signed" to "First Clean."
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Visual Status Tracking
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Document Collection
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100">
                <Star className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Reviews & Reputation</h3>
              <p className="mb-4 text-slate-600">
                Automatically request and manage client feedback. Turn happy customers into your
                best marketing channel with published reviews.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Automated Requests
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Public Review Pages
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 p-12">
            <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center text-center">
              <h2 className="mb-6 text-3xl font-bold text-white">
                Integrated with the Intelligent Core
              </h2>
              <p className="mb-8 text-lg text-slate-300">
                Your client data feeds directly into our AI. Ask the Business Assistant questions
                like "Which clients haven't been visited in 30 days?" or "Draft a renewal email for
                Downtown Offices."
              </p>
              <Link to="/sign-up">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                  Get Started Now
                </Button>
              </Link>
            </div>
            {/* Background Abstract */}
            <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_50%_120%,rgba(59,130,246,0.2),rgba(0,0,0,0))]" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
